<template>
    <div
        class="row-wrapper"
        :class="[checkClass(location, parentLocation), { 'hidden-by-search': isHiddenBySearch() }]"
    >
        <div class="row" :class="{ searched: isISearched() }">
            <div class="location" @click="show = !show">
                <div class="in-location">
                    <button
                        v-if="location.hasChildren()"
                        :class="[show ? 'icon-minus' : 'icon-plus']"
                    ></button>
                    <span :class="{ 'searched-location': isISearchedLocation() }" :title="location.name">{{
                        location.name
                    }}</span>
                </div>
            </div>

            <div class="actions">
                <template>
                    <!-- RoomConfiguration page buttons are available to all -->
                    <button
                        :class="
                            location.buttons_visibility === BUTTONS_VISIBILITY.TO_ALL
                                ? 'i-active icon-roomconf-available-on'
                                : 'i-off icon-roomconf-available-off'
                        "
                        :title="$t('roomSettings.locations.makeAvailable')"
                        @click="location.setButtonsVisibility(BUTTONS_VISIBILITY.TO_ALL)"
                    ></button>
                    <!-- RoomConfiguration page buttons are unavailable to all -->
                    <button
                        :class="
                            location.buttons_visibility === BUTTONS_VISIBILITY.TO_NONE
                                ? 'i-active icon-roomconf-unavailable-on'
                                : 'i-off icon-roomconf-unavailable-off'
                        "
                        :title="$t('roomSettings.locations.makeUnavailable')"
                        @click="location.setButtonsVisibility(BUTTONS_VISIBILITY.TO_NONE)"
                    ></button>
                    <!-- RoomConfiguration page buttons are available to super users and user administrators only -->
                    <button
                        :class="
                            location.buttons_visibility === BUTTONS_VISIBILITY.TO_SU
                                ? 'i-active icon-roomconf-superuser-on'
                                : 'i-off icon-roomconf-superuser-off'
                        "
                        :title="$t('roomSettings.locations.makeAvailableToSU')"
                        @click="location.setButtonsVisibility(BUTTONS_VISIBILITY.TO_SU)"
                    ></button>
                </template>
            </div>

            <div class="devices">
                <div
                    v-for="(module, index) in location.modules"
                    :key="index"
                    class="device"
                    :class="{ 'searched-device': isSearchedDevice(module) }"
                >
                    <div class="icon-wrapper">
                        <img :src="module.getIcon()" alt="" />
                        <template v-if="module.phonenumber">
                            <img v-if="!module.is_nuspm" :src="'static/img/devices/NISP_24px.png'" alt="speech" />
                            <span v-if="validatePhoneNumber(module.phonenumber)">{{ module.phonenumber }}</span>
                        </template>
                    </div>

                    <div class="device-name" :title="module.name">{{ module.name }}</div>
                    <span>
                        <!-- RoomConfiguration page buttons are available to all -->
                        <button
                            :class="
                                module.buttons_visibility === BUTTONS_VISIBILITY.TO_ALL
                                    ? 'i-active icon-roomconf-available-on'
                                    : 'i-off icon-roomconf-available-off'
                            "
                            :title="$t('roomSettings.locations.makeAvailable')"
                            @click="module.setButtonsVisibility(BUTTONS_VISIBILITY.TO_ALL)"
                        ></button>
                        <!-- RoomConfiguration page buttons are unavailable to all -->
                        <button
                            :class="
                                module.buttons_visibility === BUTTONS_VISIBILITY.TO_NONE
                                    ? 'i-active icon-roomconf-unavailable-on'
                                    : 'i-off icon-roomconf-unavailable-off'
                            "
                            :title="$t('roomSettings.locations.makeUnavailable')"
                            @click="module.setButtonsVisibility(BUTTONS_VISIBILITY.TO_NONE)"
                        ></button>
                        <!-- RoomConfiguration page buttons are available to super users and user administrators only -->
                        <button
                            :class="
                                module.buttons_visibility === BUTTONS_VISIBILITY.TO_SU
                                    ? 'i-active icon-roomconf-superuser-on'
                                    : 'i-off icon-roomconf-superuser-off'
                            "
                            :title="$t('roomSettings.locations.makeAvailableToSU')"
                            @click="module.setButtonsVisibility(BUTTONS_VISIBILITY.TO_SU)"
                        ></button>
                    </span>
                </div>
            </div>
        </div>
        <div v-if="location.children.length > 0" v-show="show">
            <RoomSettingsPanel
                v-for="loc in location.children"
                :key="loc.location_id"
                :location="loc"
                :parent-location="location"
                :nothing-found="nothingFound"
                :my-parent-is-searched="isSearched || myParentIsSearched"
                @contentSearched="contentSearchedHandler"
            />
        </div>
    </div>
</template>

<script type="text/babel">
import AcousticSettings from '../room-configuration/AcousticSettings.vue';
import Location from '../room-configuration/Location';
import mappingState from '../common/mappingState';
import { COMPONENT_NAMES, BUTTONS_VISIBILITY } from '../../constants';
import { getCancelToken } from '../../api';

export default {
    name: COMPONENT_NAMES.ROOM_SETTINGS_PANEL,

    components: {
        AcousticSettings,
    },

    mixins: [mappingState],

    constants: {
        BUTTONS_VISIBILITY: BUTTONS_VISIBILITY,
    },

    props: {
        location: {
            type: Location,
            required: true,
        },

        parentLocation: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        myParentIsSearched: {
            type: Boolean,
            default: false,
        },

        nothingFound: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            show: true,
            isSearched: false,
            isLinkedWithSearch: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        searchedDevices() {
            return this.searchKeyword !== '' && this.location.modules
                ? this.location.modules.filter(
                      module => module.name.toLowerCase().indexOf(this.searchKeyword) !== -1
                  )
                : null;
        },
    },

    watch: {
        searchKeyword: {
            handler(val, oldVal) {
                if (oldVal !== val) {
                    this.isSearched = false;
                    this.isLinkedWithSearch = false;
                }
            },
        },
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        isSearchedDevice(item) {
            return Array.isArray(this.searchedDevices) && this.searchedDevices.indexOf(item) !== -1;
        },

        isISearchedLocation() {
            return this.searchKeyword !== ''
                ? this.location.name.toLowerCase().indexOf(this.searchKeyword) !== -1
                : false;
        },

        /**
         * Check current location and set css class for it
         * @param location - current location
         * location types: 1 - assignment area, 2-5 - undefined, 6 - room, 7 - bed, 8 - toilet
         * @param parent - parent of current location if it exists
         * @returns class for nested level
         */
        checkClass(location, parent) {
            if (location.type === 1) {
                return '';
            }

            if (location.type === 7 || location.type === 8) {
                let filtered = parent.children.filter(item => item.type === 7 || item.type === 8);
                if (filtered.length === 1) {
                    return 'first-bed last-bed last-location';
                }
                if (filtered.length > 1) {
                    if (filtered[0].name === location.name) {
                        return 'first-bed last-location';
                    }
                    if (filtered[filtered.length - 1].name === location.name) {
                        return 'last-bed last-location';
                    }
                }
                return 'last-location';
            }
            if (parent) {
                if (parent.type === 1 && [2, 3, 4, 5, 6].includes(location.type)) {
                    return 'other-location';
                }
                if (location.type === 6 && [2, 3, 4, 5].includes(parent.type)) {
                    return 'room-location';
                }
                if (parent.type === 6 && [2, 3, 4, 5].includes(location.type)) {
                    return 'room-location';
                }
            }
        },

        isISearched() {
            let filteredModulesCount = Array.isArray(this.searchedDevices) ? this.searchedDevices.length : 0;
            let isSearched =
                this.searchKeyword !== ''
                    ? this.location.name.toLowerCase().indexOf(this.searchKeyword) !== -1 ||
                      filteredModulesCount > 0
                    : false;
            if (this.searchKeyword !== '') {
                this.isSearched = isSearched;
                if (isSearched) {
                    this.$emit('contentSearched');
                }
            }
            return isSearched;
        },

        isHiddenBySearch() {
            return (
                this.searchKeyword !== '' &&
                !this.isSearched &&
                !this.isLinkedWithSearch &&
                !this.myParentIsSearched &&
                !this.nothingFound
            );
        },

        contentSearchedHandler() {
            this.$emit('contentSearched', this.location.name);
            this.isLinkedWithSearch = true;
            this.show = true;
        },

        validatePhoneNumber(value) {
            return value !== undefined && value.length > 0;
        },
    },
};
</script>
